import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch} from 'react-redux';
import {
  MuiThemeProvider,
  createTheme
} from '@material-ui/core/styles';
import {
  Button,
  Card,
  Typography,
  IconButton,
  Icon,
  DialogContent,
  DialogTitle,
  Dialog
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import {
  getStoreTaxRatesApi,
  deleteTaxRateApi
} from 'api';
import { setEditableTaxRate } from 'redux/actions/tax_rates';
import {
  Breadcrumb,
  BackdropCircular,
  ConfirmDialog
} from 'components';
import { usStatesCodes, countriesCodes } from 'helpers/usStates';
import EditTaxRate from './EditTaxRate';


const customTheme = createTheme({
  overrides: {
    MUIDataTable: {
      root: {
        tableLayout: 'auto',
      }
    }
  }
});

const TaxRates = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { profile: {stores = []} } = useSelector((state) => state.user);

  const {
    taxRatesList,
    editableTaxRate,
    loadingTaxRates,
    taxRule
  } = useSelector((state) => state.tax_rates);

  const { settingsActiveTab } = useSelector((state) => state.store_configs);

  const storeCode = stores[0]?.storeCode;

  useEffect(() => {
    if (storeCode) {
      getStoreTaxRatesApi({dispatch, storeCode});
    }
  }, [storeCode]);

  const [openEditTaxRateDialog, setOpenEditTaxRateDialog] = useState(false);
  const [taxRateToDelete, setTaxRateToDelete] = useState(null);

  const openEditTaxRate = (taxId) => {
    const editableTax = taxRatesList.find((tax) => tax.id === taxId);
    dispatch(setEditableTaxRate(editableTax));
    setOpenEditTaxRateDialog(true);
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          routeSegments={settingsActiveTab === '#go_live_requirements' ?
            [
              { name: 'Settings', path: '/settings#go_live_requirements' },
              { name: 'Go Live Requirements', path: '/settings#go_live_requirements' },
              { name: 'Tax Rates' }
            ]
            :
            [
              { name: 'Settings', path: '/settings#store_configurations' },
              { name: 'Store Configurations', path: '/settings#store_configurations' },
              { name: 'Tax Rates' }
            ]}
        />
      </div>
      <div className="mx-10 my-5">
        <Typography variant="h4" className="my-10">Please fill in the following details for tax rate configurations.</Typography>
        <Card className="p-8 mb-10">
          <MuiThemeProvider theme={customTheme}>
            <MUIDataTable
              className="p-8"
              data={taxRatesList}
              columns={[
                {
                  name: 'code',
                  label: 'Tax Identifier',
                },
                {
                  name: 'tax_country_id',
                  label: 'Country',
                  options: {
                    filter: false,
                    sort: false,
                    display: true,
                    customBodyRenderLite: (dataIndex) => {
                      const item = taxRatesList[dataIndex];

                      return (
                        <div className="flex items-center">
                          <p className="my-0 text-15" >{countriesCodes[item.tax_country_id]}</p>
                        </div>
                      );
                    },
                  },
                },
                {
                  name: 'region_name',
                  label: 'State/Region',
                  options: {
                    filter: false,
                    sort: false,
                    display: true,
                    customBodyRenderLite: (dataIndex) => {
                      const {region_name} = taxRatesList[dataIndex];
                      return (
                        <div className="flex items-center">
                          <p className="my-0 text-15" >
                            {region_name ? usStatesCodes[region_name] : 'Any State'}
                          </p>
                        </div>
                      );
                    },
                  },
                },
                {
                  name: 'tax_postcode',
                  label: 'Zip/Post code',
                },
                {
                  name: 'rate',
                  label: 'Rate',
                  options: {
                    customBodyRenderLite: (dataIndex) => {
                      const {rate} = taxRatesList[dataIndex];
                      return (
                        <div className="flex items-center">
                          <p className="my-0 text-15" >
                            {`${rate}%`}
                          </p>
                        </div>
                      );
                    },
                  },
                },
                {
                  name: 'Action',
                  lable: 'Action',
                  options: {
                    filter: false,
                    customBodyRenderLite: (dataIndex) => {
                      const tax = taxRatesList[dataIndex];
                      return (
                        <>
                          <IconButton
                            onClick={() => openEditTaxRate(tax.id)}
                          >
                            <Icon>edit</Icon>
                          </IconButton>
                          <IconButton
                            onClick={() => setTaxRateToDelete(tax.id)}
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                        </>
                      );
                    }
                  }
                }
              ]}
              options={{
                filterArrayFullMatch: false,
                filterType: 'textField',
                responsive: 'standard',
                selectableRows: 'none',
                search: false,
                filter: false,
                download: false,
                print: false,
                pagination: true,
                viewColumns: false,
                elevation: 0,
                rowsPerPage: 20,
                rowsPerPageOptions: [20, 40, 80, 100],
              }}
            />
          </MuiThemeProvider>
        </Card>
        <Button
          variant="contained"
          color="primary"
          onClick={() => history.push({pathname: '/configurations/add-new-tax-rate'})}
        >
          Add New Tax Rate
        </Button>
      </div>
      {!!Object.keys(editableTaxRate).length &&
      <Dialog
        open={openEditTaxRateDialog}
        onClose={() => setOpenEditTaxRateDialog(false)}
        maxWidth={'lg'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" className="mt-4">Edit Tax Rate</Typography>
        </DialogTitle>
        <DialogContent  className="m-4">
          <EditTaxRate
            setOpenEditTaxRateDialog={setOpenEditTaxRateDialog}
          />
        </DialogContent>
      </Dialog>
      }
      <ConfirmDialog
        title="Delete tax rate?"
        open={Boolean(taxRateToDelete)}
        setOpen={setTaxRateToDelete}
        onConfirm={() => deleteTaxRateApi({dispatch, idTaxRate: taxRateToDelete, taxRule})}
      >
        Are you sure you want to delete the tax rate from your store?
      </ConfirmDialog>
      <BackdropCircular loading={loadingTaxRates} />
    </div>
  );
};

export default TaxRates;
